// frontend/src/components/Upload/PDFUploadZone.jsx

import React, { useState, useRef } from 'react';

const PDFUploadZone = ({ onUpload, maxSize = 10, isUploading = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Validar archivo
  const validateFile = (file) => {
    setError(null);

    // Validar tipo
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Solo se permiten archivos PDF');
      return false;
    }

    // Validar tamaño (maxSize en MB)
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(`El archivo no puede ser mayor a ${maxSize}MB`);
      return false;
    }

    return true;
  };

  // Manejar archivo seleccionado
  const handleFile = async (file) => {
    if (!validateFile(file)) {
      return;
    }

    setError(null);

    try {
      await onUpload(file);
    } catch (err) {
      setError(err.message || 'Error al subir el archivo');
    }
  };

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  // Click handler
  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  return (
    <div className="mb-4">
      {/* ✅ ZONA DE UPLOAD CON COLORES MAS+ */}
      <div
        className={`card shadow border-0 ${
          isDragging ? 'border-primary' : ''
        } ${isUploading ? 'opacity-50' : ''} ${error ? 'border-danger' : ''}`}
        style={{
          minHeight: '280px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isUploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
          border: isDragging ? '3px dashed var(--mas-primary)' : '2px dashed #dee2e6',
          backgroundColor: isDragging ? 'var(--mas-light-bg)' : '#fff',
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileInput}
          style={{ display: 'none' }}
          disabled={isUploading}
        />

        {isUploading ? (
          // ✅ ESTADO DE CARGA CON SPINNER MAS+
          <div className="text-center p-4">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Procesando...</span>
            </div>
            <h5 className="text-muted mb-2 font-weight-bold">Procesando PDF...</h5>
            <small className="text-muted">Esto puede tomar unos segundos</small>
            <div className="progress mt-3" style={{ height: '6px', maxWidth: '300px', margin: '0 auto' }}>
              <div className="progress-bar bg-gradient-primary progress-bar-striped progress-bar-animated" style={{ width: '100%' }}></div>
            </div>
          </div>
        ) : (
          // ✅ ESTADO NORMAL CON ICONOS Y COLORES MAS+
          <div className="text-center p-4">
            <div 
              className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle mb-3"
              style={{ width: '80px', height: '80px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <i className="ni ni-cloud-upload-96" style={{ fontSize: '2.5rem', color: '#fff' }}></i>
            </div>
            
            <h4 className="mt-3 mb-2 font-weight-bold text-dark">Arrastra tu PDF aquí</h4>
            <p className="text-muted mb-3 text-sm">
              o haz clic para seleccionar un archivo
            </p>
            
            <button 
              className="btn btn-primary shadow-sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              <i className="ni ni-folder-17 me-2"></i>
              Seleccionar archivo
            </button>
            
            <div className="mt-4 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
              <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
                <span className="badge badge-sm bg-gradient-info">
                  <i className="ni ni-ruler-pencil me-1"></i>
                  Máx: {maxSize}MB
                </span>
                <span className="badge badge-sm bg-gradient-success">
                  <i className="ni ni-single-copy-04 me-1"></i>
                  Solo PDF
                </span>
                <span className="badge badge-sm bg-gradient-primary">
                  <i className="ni ni-settings-gear-65 me-1"></i>
                  Procesamiento automático
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ✅ MENSAJE DE ERROR CON ESTILO MAS+ */}
      {error && (
        <div className="alert alert-danger shadow-sm mt-3 mb-0" role="alert">
          <div className="d-flex align-items-center">
            <span className="alert-icon me-2">
              <i className="ni ni-fat-remove"></i>
            </span>
            <span className="alert-text">
              <strong>Error:</strong> {error}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFUploadZone;
